import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { t } from 'i18next';

import { useParams } from 'react-router';
import useToast from 'src/hooks/useToast';
import { useUpdateTeamPositionMutation } from 'src/services/tournaments';
import { Position } from 'src/services/tournaments/getTournamentPositions';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import CustomTextField from 'src/components/forms/theme-elements/CustomTextField';

const UpdateTeamPositionForm = ({
  open,
  setOpen,
  position,
  loadPositions,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  position: Position;
  loadPositions: () => void;
}) => {
  const { id } = useParams();
  const { toastPromise } = useToast();

  const [updateTeamPosition] = useUpdateTeamPositionMutation();
  const [currentPosition, setCurrentPosition] = useState<number | undefined>(position?.position);
  const [currentPositionError, setCurrentPositionError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setCurrentPosition(position?.position);
    }
  }, [position, open]);

  const onSubmit = (values: any) => {
    console.log(values);
    if (!currentPosition) {
      setCurrentPositionError(t('TournamentPositions.teamPositionRequired'));

      return;
    }

    toastPromise({
      request: () =>
        updateTeamPosition({
          tournamentId: Number(id),
          teamId: position.id,
          position: currentPosition,
        }),
      onSuccess: (result) => {
        if ('error' in result) {
          throw result.error;
        }

        setOpen(false);
        loadPositions();

        return t('TournamentPositions.teamPositionUpdatedSuccessfully');
      },
      onError: (err) => {
        if (err.data && err.data.errors) {
          const errorMessages = Object.values(err.data.errors).flat().join('. ');

          return errorMessages;
        }
        if (err?.data?.message) {
          return err?.data?.message;
        }

        return t('TournamentScheduleTab.teamsSwapFailed');
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentPosition(undefined);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('TournamentPositions.updateTeamPosition')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('TournamentPositions.updateTeamPosition')}</DialogContentText>
        <CustomFormLabel>{t('TournamentPositions.teamPosition')}</CustomFormLabel>
        <CustomTextField
          fullWidth
          value={currentPosition}
          onChange={(e) => setCurrentPosition(Number(e.target.value))}
          type="number"
          placeholder={t('TournamentPositions.teamPosition')}
          error={!!currentPositionError}
          helperText={currentPositionError}
        />
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={handleClose}>
          {t('TournamentPositions.cancel')}
        </Button>
        <Button type="submit" onClick={onSubmit}>
          {t('TournamentPositions.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateTeamPositionForm;
